/* 203 §B.2–B.5 — THE OVERHEADS SHEET'S IMPORTER: the accountant's sheet in.
 * [RULED — Laurent, 2026-09-21: "Excel determines how many rows there are,
 * they are reflected, app computes the total per column and per year, which
 * it will use."]
 *
 * THE ROUND TRIP. §A.2.4's Excel export of the sheet IS the template (B.1:
 * "there is no second exporter"): row 1 = « Catégorie » · « TOTAL » · 1 … 12
 * in the reader's language, one category per row, numeric cells, an empty
 * cell absent. The accountant fills it — last year's figures, spread over the
 * months, then corrected as the year runs — and this modal reads it back:
 * `XLSX.read` → `sheet_to_json` (header: 1, raw) → a preview the reader
 * confirms → ONE write. Modelled on JurisdictionImportModal (the smallest
 * complete importer): parse, preview, commit; nothing is written before the
 * reader presses save.
 *
 * READ AGAINST A REAL FILE, NOT AN UNDERSTANDING OF THE FORMAT — TimeExcel's
 * lesson (its :46–97: a 94-row file that imported nothing because a time cell
 * arrives as a day fraction). What `sheet_to_json` hands over HERE, measured
 * on the export's own file (drive-203b-roundtrip.js): a numeric cell as a JS
 * number (12000), a formula cell as its cached number, an absent cell as
 * `null` (defval), a text cell as its string — so `readMoney` takes a number
 * as whole euros (floored to the euro, the sheet's own face), a string only
 * in the digit shapes a person types (« 12.000 », « 12 000 », « 12000 »), and
 * REFUSES the row on anything else, with the cell's own text in the reason
 * (166 §G.1's rule: a figure is never guessed).
 *
 * B.3 — THE ROWS ARE THE SHEET'S: each row is a category, matched by LABEL
 * (any of the four label columns, case- and whitespace-insensitively — the
 * export writes the label the sheet shows) and CREATED when it has no match
 * (the kind is FREE — taxonomyClassification.js — and the server names the
 * code from the label as it does for external_link; no migration). A category
 * the file does not mention keeps its cells for the year unless the reader
 * ticks « clear the year first » [JUDGEMENT — defaulted OFF]. A mentioned row
 * with no figure CLEARS its category's cells — the file is authoritative for
 * the rows it carries; a row with no figure and no match creates nothing.
 *
 * B.4 — THE TOTAL WINS [JUDGEMENT — advisor, overrulable]: a row carrying a
 * TOTAL is spread by E.5(a)'s own function (`spreadEvenly`, the very one a
 * typed total runs through — the euros that do not divide going one each to
 * the earliest months), whatever its month cells say; a row with months alone
 * takes them as given and its total is their sum (E.5b). The preview says,
 * per row, which arm it took.
 *
 * B.5 — THE CONFIRM STEP IS THE GUARD: before anything is written the preview
 * shows THE APP'S OWN FIGURES for the year after the import — each month's
 * column total and the year's grand, through the sheet's own readers
 * (`yearColTotal` · `yearGrand`, the definitions the sheet renders) over the
 * categories that will be on screen — beside the row count read. A sheet
 * carrying a SUBTOTAL row (a « Premises » line above its « Rent » and
 * « Insurance ») reads double there, visibly, before a save.
 *
 * B.6 — WHAT DOES NOT MOVE: the stored shape `{ year: { code: { month:
 * amount } } }` (no `total` field, ever — E.5b), the admin-only PUT
 * /api/firm-settings (the sheet's ONE writer, `onApply`, saves the whole
 * object as a typed cell does) and overheadShare.js.
 *
 * 203-glance §A, THEN THE SECOND GLANCE'S §A [RULED — Laurent, 2026-09-21:
 * "When archived something can be deleted. What then? App should check
 * whether it still exists in archive, then propose to unarchive it and
 * populate it"] — THREE STATES, NOT TWO. The sheet reads its kind WITH the
 * archived rows (CostsView, `includeArchived`) and hands them here apart from
 * the living list, and `matchCat` searches both, a living hit first. A row
 * whose label names a LIVING category is matched and imported; one naming a
 * category that is ABSENT creates it (the unknown-label arm); one naming a
 * category that is ARCHIVED AND STILL THERE is OFFERED: the row sits in the
 * preview like any other, its « Read as » reading « archived category —
 * restore and import » with a tick beside it. [JUDGEMENT — advisor,
 * overrulable] THE TICK STARTS OFF, and an unticked row is refused as the
 * first glance refused every archived row — listed under the refusals, with
 * a reason that now says the tick is the way out — because somebody archived
 * that category deliberately and a sheet mentioning it is not by itself
 * consent to undo that; the tick is the consent, given in front of the
 * figures. The count line and the save button follow the ticks live. ON
 * APPLY a ticked row restores its category and writes its figures IN ONE
 * TRANSACTION (`PUT /api/firm-settings/overheads`, the server's own
 * `$transaction`): never two round trips, never a restore that survives a
 * failed write; a plain import (nothing to restore) keeps the sheet's
 * ordinary PUT. Before the first glance, the matcher saw only the living
 * list, so such a row matched nothing and was CREATED as a second category
 * under the same label — the defect that started this. Only living
 * categories are matched, only absent ones are created.
 *
 * Hook-bearing, module scope (the Bug-A rule). React is imported for
 * `react/react-in-jsx-scope`: a file the ratchet baseline does not know must
 * carry no warning at all. */
import React, { useEffect, useMemo, useState, type Dispatch } from "react";
import { loadXlsx } from "../../lib/loadOnUse.mjs"; // 208 §1 — the Excel library loads the first time a sheet is read
import { api } from "../../api";
import { FS, RAD } from "../../lib/tokens";
import { ModalLayout } from "../SharedUI";
import Table from "../Table";
import FailedRowsList, { FAILED_ROWS_MAX, type FailedRow } from "../FailedRowsList";
import { findHeaderCol, type HeaderCell } from "../../lib/xlsxHeaders";
import { MONTHS, fcWhole, intFace, spreadEvenly, yearColTotal, yearGrand, type OhCat, type OhYear } from "../../lib/overheadsSheet";
import type { Translate } from "../../lib/interestRefusal";
import { withCount } from "../../lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count`
import ImportLossDialog from "../ImportLossDialog"; // 214 addendum 4 §25 [RULED 63] — an import asks before it loses anything
import { addLoss, hasLoss, newLossPlan, type LossPlan } from "../../lib/importLoss"; // 214 addendum 4 §25

const KIND = "overhead_category";
/* Row 1's words, one per language: the seed's three rows of `costs.oh.category`
   and `costs.oh.total` (en · fr · nl) as they stand at this commit, beside the
   reader's OWN word through `t` — so a sheet exported in one language imports
   in another, and a re-worded translation is found by the live word. */
const CATEGORY_WORDS = ["Category", "Catégorie", "Categorie"];
const TOTAL_WORDS = ["TOTAL", "TOTAAL"];
/** grouped whole euros as a person types them: « 12.000 » · « 12 000 » · « 12'000 » · « 12000 » */
const GROUPED = /^\d{1,3}(?:[ .']\d{3})+$|^\d+$/;

export type ImportArm = "total" | "months" | "cleared" | "skipped";
export interface ImportRow {
  /** the label as the file carries it */
  label: string;
  /** the matched category's code, or null: a category to create */
  code: string | null;
  /** 203-glance-2 §A — the matched category is ARCHIVED: offered for restore (the tick is the consent), refused unless ticked */
  archived: boolean;
  arm: ImportArm;
  /** the total the row will hold: the TOTAL cell, or the months' sum, or null */
  total: number | null;
  /** twelve cells, null where the row holds none */
  months: (number | null)[];
}
export interface ParsedSheet {
  /** rows with content below row 1 (blank lines not counted) */
  read: number;
  rows: ImportRow[];
  refused: FailedRow[];
  blank: number;
}
export interface MoneyCell {
  n: number | null;
  /** the cell's own text when it is not a whole-euro amount */
  bad: string | null;
}
interface Columns {
  category: number;
  total: number;
  months: number[];
}
type RowRead = { row: ImportRow } | { refused: FailedRow } | { blank: true };

const NONE: MoneyCell = { n: null, bad: null };
const normLabel = (s: string): string => s.normalize("NFC").trim().toLowerCase().replace(/\s+/g, " ");
const cellText = (v: unknown): string => {
  if (typeof v === "string") return v.trim();
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
};
const headerCell = (v: unknown): HeaderCell => (typeof v === "string" || typeof v === "number" || v == null ? v : cellText(v));
const errMsg = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** a money cell as `sheet_to_json` hands it over (see the header): whole euros, or the text that refused it */
export const readMoney = (v: unknown): MoneyCell => {
  if (v == null) return NONE;
  if (typeof v === "number") return Number.isFinite(v) && v >= 0 ? { n: Math.floor(v), bad: null } : { n: null, bad: String(v) };
  if (typeof v === "string") {
    const s = v.trim().replace(/\s+/g, " ");
    if (s === "") return NONE;
    return GROUPED.test(s) ? { n: Number(s.replace(/\D/g, "")), bad: null } : { n: null, bad: s };
  }
  return { n: null, bad: cellText(v) || "?" };
};

/** B.2 — the columns row 1 names, by the sheet's own words; null when the row is not the template's */
export const findColumns = (header: readonly HeaderCell[], words: { category: string; total: string }): Columns | null => {
  const category = findHeaderCol(header, words.category, ...CATEGORY_WORDS);
  const total = findHeaderCol(header, words.total, ...TOTAL_WORDS);
  const months = MONTHS.map((m) => findHeaderCol(header, String(m)));
  if (category < 0 || (total < 0 && months.every((c) => c < 0))) return null;
  return { category, total, months };
};

/** B.3 — matched by label, any language the entry carries; 203-glance §A: over the living AND the archived
 *  categories, a living match first — an archived hit is returned so the row can be refused, never used */
export const matchCat = (label: string, cats: readonly OhCat[]): OhCat | null => {
  const key = normLabel(label);
  const hits = cats.filter((c) => [c.label, c.labelEn, c.labelFr, c.labelNl].some((l) => typeof l === "string" && normLabel(l) === key));
  const live = hits.find((c) => c.archived !== true);
  return live ?? (hits.length ? hits[0] : null);
};

const readRow = (cells: readonly unknown[], cols: Columns, cats: readonly OhCat[], seen: Map<string, string>, t: Translate, rowNo: number): RowRead => {
  const label = cellText(cells[cols.category]);
  const total = cols.total >= 0 ? readMoney(cells[cols.total]) : NONE;
  const months = cols.months.map((c) => (c >= 0 ? readMoney(cells[c]) : NONE));
  const figures = [total, ...months];
  if (!label) {
    if (figures.every((f) => f.n == null && f.bad == null)) return { blank: true };
    return { refused: { label: t("costs.oh.import_row_n", { n: rowNo, fallback: `row ${String(rowNo)}` }), reason: t("costs.oh.import_reason_no_label", { fallback: "no category label" }) } };
  }
  const first = seen.get(normLabel(label));
  if (first !== undefined) return { refused: { label, reason: t("costs.oh.import_reason_duplicate", { label: first, fallback: `duplicate of « ${first} » above` }) } };
  seen.set(normLabel(label), label);
  const bad = figures.find((f) => f.bad != null);
  if (bad?.bad != null) return { refused: { label, reason: t("costs.oh.import_reason_not_a_number", { value: bad.bad, fallback: `not a whole-euro amount: ${bad.bad}` }) } };
  const hit = matchCat(label, cats);
  const code = hit ? hit.code : null;
  // 203-glance-2 §A — an archived hit is a ROW (offered for restore), read by the same arms; the modal refuses it until ticked
  const archived = hit?.archived === true;
  // B.4 — the total wins, and is spread by E.5(a)'s own function
  if (total.n != null) return { row: { label, code, archived, arm: "total", total: total.n, months: spreadEvenly(total.n) } };
  const given = months.map((f) => f.n);
  if (given.some((n) => n != null)) return { row: { label, code, archived, arm: "months", total: given.reduce<number>((s, n) => s + (n ?? 0), 0), months: given } };
  return { row: { label, code, archived, arm: code ? "cleared" : "skipped", total: null, months: given } };
};

/** the sheet as rows: row 1 names the columns, every other row a category (pure; the modal reads it, the drive proves it).
 *  `cats` are the living categories (matched or created), `archived` the archived ones (recognised and OFFERED — 203-glance-2 §A) */
export const parseOverheadsSheet = (aoa: readonly (readonly unknown[])[], cats: readonly OhCat[], archived: readonly OhCat[], t: Translate): ParsedSheet | { error: string } => {
  if (!aoa.length) return { error: t("costs.oh.import_empty", { fallback: "Empty file" }) };
  const words = { category: t("costs.oh.category", { fallback: "Category" }), total: t("costs.oh.total", { fallback: "TOTAL" }) };
  const cols = findColumns(aoa[0].map(headerCell), words);
  if (!cols) return { error: t("costs.oh.import_no_header", { ...words, fallback: `Malformed: row 1 must carry « ${words.category} » and « ${words.total} » or the month numbers 1 to 12` }) };
  const out: ParsedSheet = { read: 0, rows: [], refused: [], blank: 0 };
  const seen = new Map<string, string>();
  const known: readonly OhCat[] = [...cats, ...archived]; // the living first: a living match wins (matchCat)
  aoa.slice(1).forEach((cells, i) => {
    const r = readRow(cells, cols, known, seen, t, i + 2);
    if ("blank" in r) {
      out.blank += 1;
      return;
    }
    out.read += 1;
    if ("refused" in r) out.refused.push(r.refused);
    else out.rows.push(r.row);
  });
  return out;
};

/** 203-glance-2 §A — what the importer hands the sheet's writer: the year's map, and the codes of the archived
 *  categories the reader ticked — restored and written in ONE transaction by the sheet (one argument, React's own
 *  `Dispatch`: a callback type with a named parameter is a `no-unused-vars` warning in this file, 200 §G.5) */
export interface ImportApply {
  year: OhYear;
  restore: readonly string[];
}
/** a new category's key until the server names its code */
export const NEW_KEY = (i: number): string => `__new__${String(i)}`;
/** B.3 — the year after the import: the file's rows replace the categories they name, every other category keeps its cells unless the year is cleared first */
export const mergeYear = (current: OhYear, rows: readonly ImportRow[], clearFirst: boolean): OhYear => {
  const next = new Map<string, Record<string, number>>();
  if (!clearFirst) for (const [code, cells] of Object.entries(current)) next.set(code, { ...cells });
  rows.forEach((r, i) => {
    if (r.arm === "skipped") return;
    const key = r.code ?? NEW_KEY(i);
    if (r.arm === "cleared") {
      next.delete(key);
      return;
    }
    const cells: Record<string, number> = {};
    r.months.forEach((n, m) => {
      if (n != null) cells[String(m + 1)] = n;
    });
    next.set(key, cells);
  });
  return Object.fromEntries(next);
};

const armText = (r: ImportRow, t: Translate, year: string): string => {
  switch (r.arm) {
    case "total":
      return t("costs.oh.import_arm_total", { fallback: "total, spread evenly over the twelve months" });
    case "months":
      return t("costs.oh.import_arm_months", { fallback: "months as given, the total their sum" });
    case "cleared":
      return t("costs.oh.import_arm_cleared", { year, fallback: `empty: the category's ${year} cells are cleared` });
    default:
      return t("costs.oh.import_arm_skipped", { fallback: "empty and unknown: skipped, no category created" });
  }
};

const TH: React.CSSProperties = { fontSize: FS.s9, padding: "3px 5px", textAlign: "left", color: "var(--tm)", whiteSpace: "nowrap" };
const TH_R: React.CSSProperties = { ...TH, textAlign: "right" };
const TD: React.CSSProperties = { fontSize: FS.s10, padding: "2px 5px", whiteSpace: "nowrap" };
const TD_R: React.CSSProperties = { ...TD, textAlign: "right", fontFamily: "'JetBrains Mono',monospace" };
const NEW_BADGE: React.CSSProperties = { marginLeft: 6, fontSize: FS.s9, padding: "0 5px", borderRadius: RAD.r6, background: "var(--ams)", color: "var(--am)" };

interface Props {
  t: Translate;
  /** the reader's language: a created category's label is filed under it too */
  lang: string;
  file: File;
  year: string;
  /** the sheet's categories, in the sheet's order (the labels are what the rows match) */
  cats: readonly OhCat[];
  /** 203-glance(-2) §A — the kind's ARCHIVED categories: a row naming one is offered for restore, never created twice */
  archived: readonly OhCat[];
  /** the year's cells as the sheet holds them now */
  current: OhYear;
  onClose: () => void;
  /** the sheet's writer: the year on the client-held sheet, then the whole object through the existing PUT — or,
   *  with categories to restore, through the transactional `PUT /firm-settings/overheads` (203-glance-2 §A); typed
   *  by React's own one-argument `Dispatch` (a function type with a named parameter of this file's own is a
   *  `no-unused-vars` warning, 200 §G.5's class); the sheet's save state is where a refused PUT says so */
  onApply: Dispatch<ImportApply>;
}

export default function OverheadsImportModal({ t, lang, file, year, cats, archived, current, onClose, onApply }: Readonly<Props>) {
  const [aoa, setAoa] = useState<unknown[][] | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [clearFirst, setClearFirst] = useState(false);
  // 203-glance-2 §A — the archived categories the reader ticked for restore, by code (a label seen twice is refused, so a code is one row); OFF until ticked
  const [restoreTicks, setRestoreTicks] = useState<ReadonlySet<string>>(new Set());
  const [applying, setApplying] = useState(false);
  // 214 addendum 4 §25 [RULED 63] — the window's plan and the run it waits for (« Tout importer » / « Importer sans vider »)
  const [lossAsk, setLossAsk] = useState<{ plan: LossPlan; run: Dispatch<boolean> } | null>(null);
  useEffect(() => {
    let live = true;
    // 208 §1 — the Excel library loads with the file; a load that fails is the catch's words
    Promise.all([file.arrayBuffer(), loadXlsx()])
      .then(([buf, XLSX]) => {
        if (!live) return;
        const wb = XLSX.read(buf, { type: "array" });
        // the first sheet; a workbook with none throws here and the catch below shows it
        setAoa(XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: true, defval: null }));
      })
      .catch((e: unknown) => {
        if (live) setReadError(errMsg(e));
      });
    return () => {
      live = false;
    };
  }, [file]);
  const parsed = useMemo(() => (aoa ? parseOverheadsSheet(aoa, cats, archived, t) : null), [aoa, cats, archived, t]);
  const rows = useMemo(() => (parsed && !("error" in parsed) ? parsed.rows : []), [parsed]);
  const ticked = (r: ImportRow): boolean => r.code !== null && restoreTicks.has(r.code);
  /* 203-glance-2 §A — THE ROWS THAT WILL WRITE: every row but an archived one left unticked. The three readers of a
     row's index (mergeYear's NEW_KEY, the codes below, the POSTs in doImport) all walk THIS array, so a new
     category's key is the same in each. */
  const effective = useMemo(() => rows.filter((r) => !r.archived || (r.code !== null && restoreTicks.has(r.code))), [rows, restoreTicks]);
  // an archived row left unticked is refused, as the first glance refused every archived row — the reason names the tick
  const unticked = rows.filter((r) => r.archived && !ticked(r));
  const refusedAll: FailedRow[] = [
    ...(parsed && !("error" in parsed) ? parsed.refused : []),
    ...unticked.map((r) => ({ label: r.label, reason: t("costs.oh.import_reason_archived2", { fallback: "category archived: tick « restore and import » on its row to import into it" }) })),
  ];
  const next = useMemo(() => mergeYear(current, effective, clearFirst), [current, effective, clearFirst]);
  // B.5 — the categories on screen after the import: the sheet's, the archived ones restored, then the file's new ones, in file order
  const codeOnScreen = (r: ImportRow, i: number): string[] => {
    if (r.code) return r.archived ? [r.code] : []; // a living match is on the sheet already; a restored one joins it
    return r.arm === "skipped" ? [] : [NEW_KEY(i)];
  };
  const codes = [...cats.map((c) => c.code), ...effective.flatMap(codeOnScreen)];
  const colTotals = MONTHS.map((m) => yearColTotal(next, codes, m));
  const grand = yearGrand(next, codes);
  const toImport = effective.filter((r) => r.arm !== "skipped");
  const created = toImport.filter((r) => !r.code);

  /* 214 addendum 4 §25 [RULED 63 — Laurent, 04/10] — WHAT THIS IMPORT WOULD EMPTY, before anything is written: the
     year's figures of a category a row names with no figure (« cleared »), and — « Clear {year} first » ticked — of
     every category the file does not mention. Listed cell by cell under the category; the window waits; « Importer
     sans vider » writes the rows that carry figures and clears nothing; « Annuler » writes nothing. */
  const labelOfCode = (code: string): string => {
    const hit = [...cats, ...archived].find((c) => c.code === code);
    return hit?.label ?? code;
  };
  const emptiedByImport = (): LossPlan => {
    const plan = newLossPlan();
    const cellLines = (code: string, label: string) => {
      for (const [m, n] of Object.entries(current[code] ?? {})) addLoss(plan, code, label, { kind: "emptied", field: `${year}-${m.padStart(2, "0")}`, value: fcWhole(n) });
    };
    for (const r of effective) if (r.arm === "cleared" && r.code !== null) cellLines(r.code, r.label);
    if (clearFirst) {
      const named = new Set(effective.filter((r) => r.arm !== "skipped").map((r) => r.code).filter((c): c is string => c !== null));
      for (const code of Object.keys(current)) if (!named.has(code)) cellLines(code, labelOfCode(code));
    }
    return plan;
  };
  const doImport = async (keep = false) => {
    if (!parsed || "error" in parsed || applying) return;
    // the rows « sans vider » writes: a cleared row writes nothing (its index kept — NEW_KEY reads it), the year not cleared first
    const rowsToWrite = keep ? effective.map((r) => (r.arm === "cleared" ? { ...r, arm: "skipped" as const } : r)) : effective;
    const nextYear = keep ? mergeYear(current, rowsToWrite, false) : next;
    setApplying(true);
    setApplyError(null);
    try {
      const named = new Map<string, string>();
      // B.3 — created when unmatched, one at a time (the server ranks each at the end of the kind); the label in the reader's language
      for (const [i, r] of effective.entries()) {
        if (r.code || r.arm === "skipped") continue;
        const body = { kind: KIND, label: r.label, labelEn: r.label, ...(lang === "fr" ? { labelFr: r.label } : {}), ...(lang === "nl" ? { labelNl: r.label } : {}) };
        const row = (await api.post("/taxonomies", body)) as { code?: unknown } | null;
        const code = row?.code;
        if (typeof code !== "string" || !code) throw new Error(`no code for « ${r.label} »`);
        named.set(NEW_KEY(i), code);
      }
      const keyed: OhYear = {};
      for (const [k, cells] of Object.entries(nextYear)) keyed[named.get(k) ?? k] = cells;
      // 203-glance-2 §A — the ticked archived categories ride with the year: the sheet restores and writes them in one transaction
      onApply({ year: keyed, restore: effective.flatMap((r) => (r.archived && r.code !== null ? [r.code] : [])) });
      onClose();
    } catch (e: unknown) {
      setApplyError(t("costs.oh.import_failed", { msg: errMsg(e), fallback: `Failed: ${errMsg(e)}` }));
      setApplying(false);
    }
  };

  const error = readError ?? (parsed && "error" in parsed ? parsed.error : null);
  if (error != null)
    return (
      <ModalLayout title={t("modal.import.error_title", { fallback: "Import Error" })} onClose={onClose} showX={false} closeOnOverlay maxWidth={420} cancelLabel={t("common.close", { fallback: "Close" })}>
        <p style={{ fontSize: FS.s11, color: "var(--rd)" }}>{readError != null ? t("costs.oh.import_failed", { msg: error, fallback: `Failed: ${error}` }) : error}</p>
      </ModalLayout>
    );
  if (!parsed || "error" in parsed)
    return (
      <ModalLayout hideHeader footer={null} maxWidth={400}>
        <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>{t("costs.oh.import_reading", { fallback: "Reading file…" })}</p>
      </ModalLayout>
    );
  const totalWord = t("costs.oh.total", { fallback: "TOTAL" });
  if (lossAsk)
    return (
      <ImportLossDialog
        plan={lossAsk.plan}
        t={t}
        groupsSentence={t("import.loss.categories", withCount(lossAsk.plan.groups.length, "count", { fallback: `${String(lossAsk.plan.groups.length)} categories` }))}
        onAll={() => { lossAsk.run(false); }}
        onKeep={() => { lossAsk.run(true); }}
        onCancel={() => { setLossAsk(null); }}
      />
    );
  return (
    <ModalLayout
      title={t("costs.oh.import_modal_title", { year, fallback: `Import overheads ${year}` })}
      onClose={onClose}
      onSave={() => {
        const losses = emptiedByImport();
        if (hasLoss(losses)) {
          setLossAsk({ plan: losses, run: (keep) => { setLossAsk(null); void doImport(keep); } });
          return;
        }
        void doImport();
      }}
      saveDisabled={toImport.length === 0 || applying}
      saveLabel={applying ? t("costs.oh.import_applying", { fallback: "Importing…" }) : t("costs.oh.import_apply_n", withCount(toImport.length, "n", { fallback: `Import ${String(toImport.length)} rows` }))}
      showX={false}
      closeOnOverlay
      maxWidth={1100}
    >
      <p style={{ fontSize: FS.s11, marginBottom: 10 }}>
        {/* 209 §7 [RULED 16] — one sentence per number, each in its language's plural, in the sentence's frame */}
        {t("costs.oh.import_summary_frame", {
          read: t("costs.oh.summary_read", { count: parsed.read, fallback: `${String(parsed.read)} rows read` }),
          imported: t("costs.oh.summary_to_import", { count: toImport.length, fallback: `${String(toImport.length)} to import` }),
          created: t("costs.oh.summary_created", { count: created.length, fallback: `${String(created.length)} new categories` }),
          refused: t("costs.oh.summary_refused", { count: refusedAll.length, fallback: `${String(refusedAll.length)} refused` }),
          blank: t("costs.oh.summary_blank", { count: parsed.blank, fallback: `${String(parsed.blank)} blank lines skipped` }),
          fallback: "{read}: {imported} ({created}), {refused}, {blank}",
        })}
      </p>
      {applyError != null && <p style={{ fontSize: FS.s11, color: "var(--rd)", marginBottom: 8 }}>{applyError}</p>}
      {rows.length > 0 && (
        <div className="table-wrap" style={{ maxHeight: 260, overflow: "auto" }}>
          <Table style={{ width: "100%" }}>
            <thead>
              <tr>
                <th style={TH}>{t("costs.oh.category", { fallback: "Category" })}</th>
                <th style={TH}>{t("costs.oh.import_col_read_as", { fallback: "Read as" })}</th>
                <th style={TH_R}>{totalWord}</th>
                {MONTHS.map((m) => (
                  <th key={m} style={TH_R}>{m}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderTop: "1px solid var(--br)", opacity: r.arm === "skipped" || (r.archived && !ticked(r)) ? 0.55 : 1 }}>
                  <td style={{ ...TD, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {r.label}
                    {!r.code && r.arm !== "skipped" && <span style={NEW_BADGE}>{t("costs.oh.import_new", { fallback: "new" })}</span>}
                  </td>
                  <td style={{ ...TD, color: "var(--tm)" }}>
                    {r.archived && r.code !== null ? (
                      /* 203-glance-2 §A — the offer: the tick is the consent to restore, off until given */
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={ticked(r)}
                          onChange={(e) => {
                            const code = r.code;
                            if (code === null) return;
                            const on = e.target.checked;
                            setRestoreTicks((prev) => {
                              const n = new Set(prev);
                              if (on) n.add(code);
                              else n.delete(code);
                              return n;
                            });
                          }}
                        />
                        {t("costs.oh.import_arm_archived", { fallback: "archived category — restore and import" })}
                      </label>
                    ) : (
                      armText(r, t, year)
                    )}
                  </td>
                  <td style={{ ...TD_R, fontWeight: 600 }}>{intFace(r.total)}</td>
                  {r.months.map((n, m) => (
                    <td key={m} style={TD_R}>{intFace(n)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      <FailedRowsList
        title={t("costs.oh.import_refused_title", { fallback: "Refused rows, not imported" })}
        rows={refusedAll}
        moreLabel={t("app.import.more_n", withCount(Math.max(0, refusedAll.length - FAILED_ROWS_MAX), "n", { fallback: `… +${String(Math.max(0, refusedAll.length - FAILED_ROWS_MAX))} more` }))}
      />
      {/* B.5 — the guard: the app's own figures for the year after this import, before anything is written */}
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: FS.s11, fontWeight: 600, marginBottom: 4 }}>
          {t("costs.oh.import_computed", withCount(codes.length, "n", { year, fallback: `What the app will use for ${year} after this import, over ${String(codes.length)} categories:` }))}
        </div>
        <div className="table-wrap" style={{ overflowX: "auto" }}>
          <Table style={{ width: "100%" }}>
            <thead>
              <tr>
                <th style={TH_R}>{totalWord}</th>
                {MONTHS.map((m) => (
                  <th key={m} style={TH_R}>{m}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ ...TD_R, fontWeight: 700, color: "var(--ac)" }}>{fcWhole(grand)}</td>
                {colTotals.map((v, i) => (
                  <td key={i} style={{ ...TD_R, fontWeight: 600 }}>{fcWhole(v)}</td>
                ))}
              </tr>
            </tbody>
          </Table>
        </div>
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, fontSize: FS.s11, cursor: "pointer" }}>
        <input
          type="checkbox"
          checked={clearFirst}
          onChange={(e) => {
            setClearFirst(e.target.checked);
          }}
        />
        {t("costs.oh.import_clear_year", { year, fallback: `Clear ${year} first: categories the file does not mention lose their figures` })}
      </label>
    </ModalLayout>
  );
}
